import {
  ancoragensDeFitas,
  calcularAncoragem,
  comprimentoDaCorda,
  definicaoDaFuncao,
  FUNCOES_ESCOLHIVEIS,
  pontosDeAncoragem,
  type CenaTabletop,
  type CordaTabletop,
  type FuncaoCorda,
  type ReferenciaPonto,
} from '../../dominio/tabletop'
import { IconeCorda, IconeEnvolvimento, IconeFechar, IconeMosquetao, IconePolia, IconePoliaDupla, IconeNoFiel, IconeNoOito, IconeRemover, IconeSistemaVM } from '../icones'
import type { PecaParaPrender } from '../../dominio/tabletop/prenderNoPonto'
import type { LadoDaPonta, NoNaPonta } from '../../dominio/tabletop/nosNasPontas'

/**
 * As barras da corda sobre a cena, no lugar da barra de ação rápida da peça:
 *
 *  - enquanto se toca nos pontos, a de desenho diz o que tocar a seguir e tem
 *    Desfazer, Pronto e Cancelar — o painel de Cordas fica fechado, a cena livre;
 *  - com uma corda selecionada, a de opções pergunta o que ela é (as funções)
 *    e deixa inverter a seta e remover;
 *  - com um ponto de ancoragem tocado, a do ponto oferece o que prender nele.
 */

const nomeDoPonto = (cena: CenaTabletop, referencia: ReferenciaPonto): string => {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  const ponto = objeto && pontosDeAncoragem(objeto).find((p) => p.id === referencia.idPonto)
  return ponto ? `${objeto!.nome} · ${ponto.nome}` : referencia.idPonto
}

/**
 * O resumo da ancoragem cujo ponto central é este ponto: o tipo, o ângulo
 * entre as pernas e a carga por perna, numa linha. `undefined` se o ponto não
 * é centro de ancoragem.
 */
export function resumoDaAncoragemNoPonto(cena: CenaTabletop, ponto: ReferenciaPonto): string | undefined {
  const mesmo = (r: ReferenciaPonto) => r.idObjeto === ponto.idObjeto && r.idPonto === ponto.idPonto
  const ancoragem = (cena.ancoragens ?? []).find((a) => mesmo(a.centro)) ?? ancoragensDeFitas(cena).find((a) => mesmo(a.centro))
  if (!ancoragem) return undefined
  const r = calcularAncoragem(cena, ancoragem)
  if (r.incompleta) return `${ancoragem.tipo} incompleta`
  const graus = `${Math.round(r.anguloMaximoGraus!)}°`
  if (r.degenerada) return `${ancoragem.tipo} ${graus} · pernas opostas`
  const carga =
    r.equalizacao === 'pre-equalizada'
      ? r.fracoesPorPerna!.map((f) => `${Math.round(f * 100)}%`).join(' / ')
      : `${Math.round(r.fracaoPorPerna! * 100)}% por perna`
  return `${ancoragem.tipo} ${graus} · ${carga}`
}

/** O que tocar agora, conforme o que já foi tocado. */
export function dicaDoDesenho(funcao: FuncaoCorda, pontos: number): string {
  if (funcao === 'fita') {
    return pontos === 0
      ? 'Fita: toque no ponto que ela abraça (o banzo, o toco, a viatura).'
      : pontos === 1
        ? 'Toque no ponto em que ela termina (o mosquetão, o olhal da polia), ou em Pronto para deixar uma fita solta: uma alça de ancoragem sozinha.'
        : 'Toque em Pronto para fechar a fita.'
  }
  if (pontos === 0) return 'Corda: toque no ponto de ancoragem onde ela começa.'
  if (pontos === 1) return 'Toque no próximo ponto. Numa polia, toque na entrada ou na saída: a corda passa pela roldana.'
  return 'Continue tocando nos pontos por onde ela passa, ou toque em Pronto.'
}

export function BarraDesenhoCorda({
  cena,
  funcao,
  percurso,
  aoDesfazerPonto,
  aoConcluir,
  aoCancelar,
}: {
  cena: CenaTabletop
  funcao: FuncaoCorda
  percurso: readonly ReferenciaPonto[]
  aoDesfazerPonto: () => void
  aoConcluir: () => void
  aoCancelar: () => void
}) {
  const ultimo = percurso[percurso.length - 1]
  return (
    <div className="tt-banner-posicionamento tt-barra-corda" role="group" aria-label="Prendendo a corda nos pontos">
      <span className="tt-barra-corda__texto" aria-live="polite">
        {dicaDoDesenho(funcao, percurso.length)}
        {ultimo && <span className="tt-barra-corda__ultimo"> Último: {nomeDoPonto(cena, ultimo)}.</span>}
      </span>
      <button type="button" disabled={percurso.length === 0} onClick={aoDesfazerPonto}>
        Desfazer ponto
      </button>
      <button type="button" className="btn-acao-cad btn-acao-cad--destaque" disabled={percurso.length < (funcao === 'fita' ? 1 : 2)} onClick={aoConcluir}>
        Pronto
      </button>
      <button type="button" onClick={aoCancelar}>
        Cancelar
      </button>
    </div>
  )
}

const formatarMetros = (m: number) => `${m.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} m`

export function BarraCordaSelecionada({
  cena,
  corda,
  aoEscolherFuncao,
  aoInverterSentido,
  aoMoverPonta,
  aoMosquetaoNoV,
  aoAmarrarPontas,
  aoRemover,
  aoFechar,
  noInspetor = false,
}: {
  cena: CenaTabletop
  corda: CordaTabletop
  /** Dentro do inspetor lateral: sem flutuar sobre a cena. */
  noInspetor?: boolean
  /** Só quando alguma ponta está presa num ponto de ancoragem sem nó: pergunta o nó de cada uma. */
  aoAmarrarPontas?: () => void
  aoEscolherFuncao: (funcao: FuncaoCorda) => void
  aoInverterSentido: () => void
  /** Mudar o início ou o fim da corda para outro ponto, sem refazê-la. */
  aoMoverPonta?: (lado: 'inicio' | 'fim') => void
  /** Só numa fita de dois pontos entre estruturas: vira o V, com o mosquetão no vértice. */
  aoMosquetaoNoV?: () => void
  aoRemover: () => void
  aoFechar: () => void
}) {
  const semFuncao = corda.funcao === 'livre'
  return (
    <div
      className={noInspetor ? 'tt-barra-corda-inspetor' : 'tt-banner-posicionamento tt-barra-corda tt-barra-corda--opcoes'}
      role="group"
      aria-label="Opções da corda"
    >
      <span className="tt-barra-corda__texto">
        <strong>{semFuncao ? 'O que é esta corda?' : definicaoDaFuncao(corda.funcao).rotulo}</strong>
        <span className="tt-barra-corda__ultimo"> {formatarMetros(comprimentoDaCorda(cena, corda))}</span>
      </span>
      <div className="tt-barra-corda__funcoes" role="radiogroup" aria-label="Função da corda">
        {FUNCOES_ESCOLHIVEIS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="radio"
            aria-checked={corda.funcao === f.id}
            className={`tt-chip-funcao${corda.funcao === f.id ? ' tt-chip-funcao--ativa' : ''}`}
            style={{ ['--cor-funcao' as string]: f.cor }}
            onClick={() => aoEscolherFuncao(f.id)}
            title={f.descricao}
          >
            <span className="tt-chip-funcao__cor" aria-hidden="true" />
            {f.rotulo}
          </button>
        ))}
      </div>
      {!semFuncao && corda.funcao !== 'fita' && (
        <button type="button" onClick={aoInverterSentido} title="Inverter a seta de puxada">
          {corda.sentido ? 'Inverter seta' : 'Pôr seta'}
        </button>
      )}
      {aoMosquetaoNoV && (
        <button type="button" onClick={aoMosquetaoNoV} title="Fazer o V da fita e clipar um mosquetão no vértice">
          <IconeMosquetao />
          Mosquetão no V
        </button>
      )}
      {aoAmarrarPontas && (
        <button type="button" onClick={aoAmarrarPontas} title="Escolher o nó com que a corda se prende nos pontos de ancoragem">
          <IconeNoOito />
          Nó nas pontas
        </button>
      )}
      {aoMoverPonta && (
        <>
          <button type="button" onClick={() => aoMoverPonta('inicio')} title={`Levar o início para outro ponto (hoje: ${nomeDoPonto(cena, corda.percurso[0]!)})`}>
            Mover início
          </button>
          <button
            type="button"
            onClick={() => aoMoverPonta('fim')}
            title={`Levar o fim para outro ponto (hoje: ${nomeDoPonto(cena, corda.percurso[corda.percurso.length - 1]!)})`}
          >
            Mover fim
          </button>
        </>
      )}
      <button type="button" className="btn-acao-cad btn-acao-cad--perigo" onClick={aoRemover} title="Remover a corda" aria-label="Remover a corda">
        <IconeRemover />
      </button>
      <button type="button" onClick={aoFechar}>
        {semFuncao ? 'Depois' : 'Fechar'}
      </button>
    </div>
  )
}

/**
 * As ações de um ponto de ancoragem tocado na cena: começar a montagem pelo
 * ponto, como numa mesa. A corda e a fita nascem presas nele e seguem pelos
 * próximos toques; o mosquetão e a polia penduram nele (numa estrutura, por
 * uma fita).
 */
export function BarraPontoAncoragem({
  cena,
  ponto,
  aoPrenderCorda,
  aoPrenderFita,
  aoPrenderPeca,
  aoPrenderNoComCorda,
  aoJuntarComOutraFita,
  aoFechar,
  resumo,
}: {
  cena: CenaTabletop
  ponto: ReferenciaPonto
  aoPrenderCorda: () => void
  aoPrenderFita: () => void
  aoPrenderPeca: (peca: PecaParaPrender, parametros?: Record<string, number>) => void
  aoPrenderNoComCorda?: (no: 'noOito' | 'noFiel') => void
  aoJuntarComOutraFita?: () => void
  aoFechar: () => void
  /** Quando o ponto é o centro de uma ancoragem: o ângulo e a carga por perna, aqui em vez de sobre a cena. */
  resumo?: string
}) {
  const objeto = cena.objetos.find((o) => o.id === ponto.idObjeto)
  const definicao = objeto && pontosDeAncoragem(objeto).find((p) => p.id === ponto.idPonto)
  // num ponto de passagem (a entrada de uma polia) só passa corda; pendurar peça é nos de ancoragem e carga
  const prendePeca = definicao?.uso !== 'passagem'
  const ehPontoDeFita = (cena.cordas ?? []).some(
    (c) => c.funcao === 'fita' && c.percurso.some((p) => p.idObjeto === ponto.idObjeto && p.idPonto === ponto.idPonto),
  )

  return (
    <div className="tt-banner-posicionamento tt-barra-corda tt-barra-ponto" role="group" aria-label="Ações do ponto de ancoragem">
      <span className="tt-barra-corda__texto">
        <strong>{definicao?.nome ?? ponto.idPonto}</strong>
        <span className="tt-barra-corda__ultimo"> · {objeto?.nome ?? ponto.idObjeto}</span>
        {resumo && <span className="tt-barra-corda__ultimo"> · {resumo}</span>}
      </span>

      {/* 1. Cordas e Fitas */}
      <button type="button" onClick={aoPrenderCorda} title="Prender uma corda aqui e seguir tocando nos pontos por onde ela passa">
        <IconeCorda />
        Prender corda
      </button>
      <button type="button" onClick={aoPrenderFita} title="Prender uma fita aqui e tocar no ponto em que ela termina">
        <IconeEnvolvimento />
        Prender fita
      </button>

      {/* 2. Juntar com outra fita para formar ancoragem em V */}
      {ehPontoDeFita && aoJuntarComOutraFita && (
        <button
          type="button"
          onClick={aoJuntarComOutraFita}
          title="Juntar esta fita com outra fita para formar um ponto de ancoragem diretor em V"
          className="btn-destaque-acao"
        >
          <IconeEnvolvimento />
          Juntar com outra fita
        </button>
      )}

      {/* 3. Equipamentos no ponto (quando permitido) */}
      {prendePeca && (
        <>
          {/* Nós */}
          <button
            type="button"
            onClick={() => (aoPrenderNoComCorda ? aoPrenderNoComCorda('noOito') : aoPrenderPeca('noOito'))}
            title="Amarrar um nó em oito duplo aqui e seguir com a corda"
          >
            <IconeNoOito />
            Nó em oito
          </button>
          <button
            type="button"
            onClick={() => (aoPrenderNoComCorda ? aoPrenderNoComCorda('noFiel') : aoPrenderPeca('noFiel'))}
            title="Amarrar a ponta da corda aqui com um nó fiel (direto no degrau, no banzo ou na barra)"
          >
            <IconeNoFiel />
            Amarrar fiel
          </button>

          {/* Polias */}
          <button type="button" onClick={() => aoPrenderPeca('polia')} title="Pendurar uma polia simples aqui (numa estrutura, por uma fita)">
            <IconePolia />
            Pendurar polia
          </button>
          <button type="button" onClick={() => aoPrenderPeca('poliaDupla')} title="Pendurar uma polia dupla aqui (numa estrutura, por uma fita)">
            <IconePoliaDupla />
            Polia dupla
          </button>

          {/* Sistemas de Redução de Força */}
          <button
            type="button"
            onClick={() => aoPrenderPeca('sistemaVM')}
            title="Pendurar um sistema de vantagem (redução de força) aqui, pelo bloco fixo (numa estrutura, por uma fita)"
          >
            <IconeSistemaVM />
            Sistema de vantagem
          </button>
          <button
            type="button"
            onClick={() => aoPrenderPeca('sistemaVM', { razao: 2 })}
            title="Pendurar um sistema de redução 2:1 simples aqui"
          >
            <IconeSistemaVM />
            Sistema 2:1
          </button>
          <button
            type="button"
            onClick={() => aoPrenderPeca('sistemaVM', { razao: 4 })}
            title="Pendurar um sistema de redução 4:1 duplo aqui"
          >
            <IconeSistemaVM />
            Sistema 4:1
          </button>

          {/* Mosquetão */}
          <button type="button" onClick={() => aoPrenderPeca('mosquetao')} title="Clipar um mosquetão aqui (numa estrutura, por uma fita)">
            <IconeMosquetao />
            Clipar mosquetão
          </button>
        </>
      )}

      <button type="button" onClick={aoFechar} aria-label="Fechar painel do ponto">
        <IconeFechar />
        Fechar
      </button>
    </div>
  )
}


const NOME_DO_LADO: Record<LadoDaPonta, string> = { inicio: 'início', fim: 'fim' }

/**
 * A pergunta do nó: a corda chegou num ponto de ancoragem, e o instrutor
 * escolhe como ela se prende ali. Uma ponta de cada vez (início, depois fim).
 */
export function BarraNoNaPonta({
  cena,
  idCorda,
  lado,
  aoEscolher,
  aoFechar,
}: {
  cena: CenaTabletop
  idCorda: string
  lado: LadoDaPonta
  aoEscolher: (no: NoNaPonta | null) => void
  aoFechar: () => void
}) {
  const corda = cena.cordas?.find((c) => c.id === idCorda)
  if (!corda) return null
  const ponta = lado === 'inicio' ? corda.percurso[0]! : corda.percurso[corda.percurso.length - 1]!
  return (
    <div className="tt-banner-posicionamento tt-barra-corda tt-barra-ponto" role="group" aria-label={`Nó no ${NOME_DO_LADO[lado]} da corda`}>
      <span className="tt-barra-corda__texto">
        <strong>Qual nó no {NOME_DO_LADO[lado]}?</strong>
        <span className="tt-barra-corda__ultimo"> · {nomeDoPonto(cena, ponta)}</span>
      </span>
      <button type="button" onClick={() => aoEscolher('noOito')} title="Oito duplo / oito encaixado: a alça do oito presa no ponto">
        <IconeNoOito />
        Nó oito
      </button>
      <button type="button" onClick={() => aoEscolher('noFiel')} title="Volta do fiel: a volta abraça o ponto (degrau, banzo, mosquetão)">
        <IconeNoFiel />
        Volta do fiel
      </button>
      <button type="button" onClick={() => aoEscolher(null)} title="A corda fica presa no ponto sem nó desenhado">
        Sem nó
      </button>
      <button type="button" onClick={aoFechar}>
        Depois
      </button>
    </div>
  )
}
